import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export interface LabeledIconCellProps {
  /** Leading 16×16 icon (typically `icons/main` glyph). */
  icon: ReactNode;
  /** Cell label. Truncates with ellipsis when narrower than its container. */
  label: string;
  className?: string;
  /** Override label typography (size/leading/weight). Defaults to `text-p1`. */
  labelClassName?: string;
}

/**
 * `LabeledIconCell` — `icons/main` glyph + truncating label per Figma
 * `289:45773`. Used as the leading cell of table rows (Document type, File
 * type, etc.).
 *
 * The cell wraps in `min-w-0` so flex-child truncation works; the label
 * surfaces the full text via `title` for hover-to-read accessibility on
 * truncated rows.
 */
export function LabeledIconCell({ icon, label, className, labelClassName }: LabeledIconCellProps) {
  return (
    <div
      className={cn('flex items-center gap-2 min-w-0', className)}
      data-testid="labeled-icon-cell"
    >
      <span
        data-testid="labeled-icon-cell-icon-slot"
        className="inline-flex w-4 h-4 items-center justify-center shrink-0 text-dawn-black"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span
        data-testid="labeled-icon-cell-label"
        title={label}
        className={cn('text-p1 text-dawn-black truncate', labelClassName)}
      >
        {label}
      </span>
    </div>
  );
}

LabeledIconCell.displayName = 'LabeledIconCell';
